const INTERNAL_THEME_IDS = [
  'academic',
  'tiny-aya-dark',
  'north-dark',
  'north-light',
  'embed-dark',
  'embed-light',
  'command-dark',
  'command-light',
  'rerank-dark',
  'rerank-light',
];
const EXTERNAL_THEME_ID = 'external-default';

export const EXTERNAL_PRIMARY_COLORS = [
  { value: '#2000DF', label: 'Primary 1' },
  { value: '#007840', label: 'Primary 2' },
  { value: '#BF0040', label: 'Primary 3' },
];

export const DEFAULT_EXTERNAL_PRIMARY_COLOR = EXTERNAL_PRIMARY_COLORS[0].value;

export const EXTERNAL_BACKGROUND_COLORS = [
  { value: '#FFFFFF', label: 'White' },
  { value: '#F1F5F9', label: 'Light Gray' },
  { value: '#1E293B', label: 'Navy' },
  { value: '#1A3A2A', label: 'Forest' },
  { value: '#000000', label: 'Black' },
];

export const DEFAULT_EXTERNAL_BACKGROUND_COLOR = '#FFFFFF';

export const INTERNAL_FONT = {
  webStack: 'Cohere Text',
  reactFallbackStack: 'Cohere Text, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  labelFamily: 'Unica77 Cohere',
  labelFallbackStack: 'Unica77 Cohere, Cohere Text, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  numberFamily: 'Cohere Text',
  numberFallbackStack: 'Cohere Text, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  pythonFamily: 'Cohere Text',
  pythonFileName: 'CohereText-Regular.ttf',
  pythonLabelFamily: 'Unica77 Cohere',
  pythonLabelFileName: 'internal/Unica77Cohere-Regular.otf',
  pythonNumberFamily: 'Cohere Text',
  pythonNumberFileName: 'CohereText-Regular.ttf',
};

export const EXTERNAL_FONT = {
  webStack: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  reactFallbackStack: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  labelFamily: 'Inter',
  labelFallbackStack: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  numberFamily: 'Inter',
  numberFallbackStack: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  pythonFamily: 'Inter',
  pythonFileName: 'Inter-Regular.ttf',
  pythonLabelFamily: 'Inter',
  pythonLabelFileName: 'Inter-Regular.ttf',
  pythonNumberFamily: 'Inter',
  pythonNumberFileName: 'Inter-Regular.ttf',
};

const deploymentId = process.env.REACT_APP_DEPLOYMENT === 'external' ? 'external' : 'internal';

export const deploymentProfile = {
  id: deploymentId,
  isExternal: deploymentId === 'external',
  isInternal: deploymentId !== 'external',
  defaultThemeId: deploymentId === 'external' ? EXTERNAL_THEME_ID : 'academic',
  allowedThemeIds: deploymentId === 'external' ? [EXTERNAL_THEME_ID] : INTERNAL_THEME_IDS,
  allowThemeImport: deploymentId !== 'external',
  allowCustomThemes: deploymentId !== 'external',
  showThemeSection: deploymentId !== 'external',
  externalPrimaryColors: EXTERNAL_PRIMARY_COLORS,
  defaultPrimaryColor: DEFAULT_EXTERNAL_PRIMARY_COLOR,
  font: deploymentId === 'external' ? EXTERNAL_FONT : INTERNAL_FONT,
};

function isInternalAllowedThemeId(themeId, allThemes = {}) {
  if (!themeId || themeId === EXTERNAL_THEME_ID) return false;
  return Boolean(allThemes[themeId]);
}

export function isAllowedThemeId(themeId, allThemes = {}) {
  if (deploymentProfile.isInternal) {
    return isInternalAllowedThemeId(themeId, allThemes);
  }
  return deploymentProfile.allowedThemeIds.includes(themeId);
}

export function sanitizeThemeId(themeId, allThemes = {}) {
  return isAllowedThemeId(themeId, allThemes) ? themeId : deploymentProfile.defaultThemeId;
}

export function getAllowedThemes(allThemes) {
  if (deploymentProfile.isInternal) {
    return Object.fromEntries(
      Object.entries(allThemes).filter(([themeId]) => isInternalAllowedThemeId(themeId, allThemes))
    );
  }
  return Object.fromEntries(
    Object.entries(allThemes).filter(([themeId]) => isAllowedThemeId(themeId))
  );
}

export function isExternalColorControl(control) {
  return (
    control.type === 'color' ||
    control.type === 'paletteGroup' ||
    control.type === 'externalColor' ||
    control.key === 'colorPalette'
  );
}

export function applyDeploymentChartConfig(config = {}, previousConfig = {}) {
  if (!deploymentProfile.isExternal) return { ...config };

  const primaryColor = previousConfig.primaryColor || config.primaryColor || deploymentProfile.defaultPrimaryColor;
  const externalColorMode = previousConfig.externalColorMode ?? config.externalColorMode ?? 'single';

  return {
    ...config,
    primaryColor,
    color: primaryColor,
    barColor: primaryColor,
    lineColor: primaryColor,
    positiveColor: primaryColor,
    negativeColor: primaryColor,
    highlightColor: primaryColor,
    externalColorMode,
    useBluesPalette: false,
    useRedsPalette: false,
    useGreensPalette: false,
  };
}

